import { type AuthUser } from '../../../hooks/use-auth'
import { AuthFunctions, hasPermission } from '../../../utils/permissions'
import { AppRoutes } from '../../Routes'

export type P2PSurface = 'collection' | 'completions' | 'invitations' | 'dashboard' | 'statewide'

export const P2P_SURFACE_SEGMENT: Record<P2PSurface, string> = {
  collection: 'collection',
  completions: 'completions',
  invitations: 'invitations',
  dashboard: 'dashboard',
  statewide: 'statewide'
}

export const P2P_SURFACE_LABEL: Record<P2PSurface, string> = {
  collection: 'Collection',
  completions: 'Form Completions',
  // This surface is labelled "Overview" in the UI, but its internal key and URL
  // segment stay 'invitations'.
  invitations: 'Overview',
  dashboard: 'Data Dashboard',
  statewide: 'Statewide'
}

/**
 * Highest-priority surface first. This ordering drives both the default landing surface
 * (index 0 of a user's entitlements) and the left-to-right tab order, so the default
 * surface is always the first tab: Overview → Form Completions → Data Dashboard.
 */
const SURFACE_PRIORITY: P2PSurface[] = ['statewide', 'invitations', 'completions', 'dashboard', 'collection']

const anyOf = (user: AuthUser, ...functions: string[]): boolean => functions.some(f => hasPermission(user, f))

/**
 * Per-permission entitlements; a user's entitled surfaces are the union across the
 * permissions they hold, returned in priority order.
 */
export const getEntitledSurfaces = (user: AuthUser): P2PSurface[] => {
  const surfaces = new Set<P2PSurface>()

  if (anyOf(user, AuthFunctions.PEER_TO_PEER_PARTICIPATION, AuthFunctions.PEER_TO_PEER_PARTICIPATION_EDIT)) {
    surfaces.add('collection')
  }
  if (anyOf(user, AuthFunctions.ISD_ADMINISTRATION, AuthFunctions.ISD_ADMINISTRATION_EDIT)) {
    surfaces.add('completions')
    surfaces.add('invitations')
    surfaces.add('dashboard')
  }
  if (anyOf(user, AuthFunctions.PEER_TO_PEER_DATA_MANAGEMENT, AuthFunctions.PEER_TO_PEER_DATA_MANAGEMENT_EDIT)) {
    surfaces.add('statewide')
    surfaces.add('dashboard')
  }
  if (anyOf(user, AuthFunctions.RCN_ADMINISTRATION, AuthFunctions.RCN_ADMINISTRATION_EDIT)) {
    surfaces.add('completions')
    surfaces.add('dashboard')
  }

  return SURFACE_PRIORITY.filter(s => surfaces.has(s))
}

export const getDefaultSurface = (user: AuthUser): P2PSurface | null => getEntitledSurfaces(user)[0] ?? null

export const p2pSurfaceUrl = (surface: P2PSurface): string => `${AppRoutes.PEER_TO_PEER}/${P2P_SURFACE_SEGMENT[surface]}`

/**
 * The Completions surface lets the caller send reminders to outstanding districts. ISD,
 * RCN, and Data-Manager admins can all act — the backend Remind endpoints authorize all
 * three, an RCN reminding the outstanding districts within its scope.
 */
export const canManageCompletions = (user: AuthUser): boolean =>
  anyOf(user,
    AuthFunctions.ISD_ADMINISTRATION, AuthFunctions.ISD_ADMINISTRATION_EDIT,
    AuthFunctions.RCN_ADMINISTRATION, AuthFunctions.RCN_ADMINISTRATION_EDIT,
    AuthFunctions.PEER_TO_PEER_DATA_MANAGEMENT, AuthFunctions.PEER_TO_PEER_DATA_MANAGEMENT_EDIT)
